import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/new-business-website-checklist";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;
const TITLE = "New Business Website Checklist — Launch in 30 Days (2026)";
const DESC =
  "Exact 30-day checklist for a new Colorado business website: domain, hosting, content, GBP, schema, and the first 10 backlinks. Built by a 25-year local agency.";

export const Route = createFileRoute("/learn/new-business-website-checklist")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: TITLE, description: DESC }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="10 min read"
      title="New business website checklist: launch in 30 days"
      intro="A new Colorado business has roughly 30 days from filing the LLC to needing a working website that converts. Here's the exact checklist we use with first-time owners — every item, in launch order."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "New business website checklist" },
      ]}
      path={PATH}
      faqs={[
        { q: "Can I really launch a new business website in 30 days?", a: "Yes — a 5–10 page Colorado service business site can be live in 3–4 weeks if content and photos are ready. Delays almost always come from waiting on the owner for copy and pictures, not the build itself." },
        { q: "What do I need before I hire a web designer?", a: "Your LLC filing, domain name registered in your own name, business email, logo (even a simple one), 8–15 photos of you and your work, and a written list of services with pricing or 'starting at' ranges." },
        { q: "Do I need a logo before launch?", a: "A clean wordmark logo is enough for launch — you can refine later. Don't delay launching by 3 months waiting for a $5,000 brand identity. Revenue first, polish second." },
        { q: "What's the single most important thing to get right?", a: "Phone number and contact form working on mobile. We audit hundreds of sites a year and the #1 failure is a broken or hidden CTA on a phone. Test it from your own phone before launch." },
      ]}
    >
      <h2>Week 1: legal + foundation</h2>
      <ul>
        <li>File LLC with <a href="https://www.coloradosos.gov/biz/" target="_blank" rel="noopener noreferrer">Colorado Secretary of State</a> ($50). <Link to="/learn/start-a-business-in-colorado">Full guide.</Link></li>
        <li>Get EIN from IRS (free, 10 minutes).</li>
        <li>Register <strong>yourbusiness.com</strong> in your own name at Namecheap or Cloudflare ($12–$20/year).</li>
        <li>Set up business email (Google Workspace $6/user/month).</li>
        <li>Claim and verify Google Business Profile. <Link to="/learn/google-business-profile">GBP setup.</Link></li>
        <li>Open business bank account.</li>
      </ul>

      <h2>Week 2: content + assets</h2>
      <ul>
        <li>Write 1 sentence answers to: <em>What do you do? Who do you do it for? What makes you different? Where do you work?</em></li>
        <li>List every service you offer (don't underestimate — each missing service is a missed search).</li>
        <li>Take 15–25 real photos: you, your team, your truck/storefront, before/afters of recent work.</li>
        <li>Collect 3–5 customer testimonials with full names and cities.</li>
        <li>Pick build path: <Link to="/learn/custom-web-design-vs-diy-platforms">DIY platform, freelancer, or custom agency.</Link></li>
      </ul>

      <h2>Week 3: build</h2>
      <ul>
        <li>Home page with a clear headline answering "who you are, what you do, where you work."</li>
        <li>Services pages — one per major service (not a single combined page).</li>
        <li>About page with real photos and the founder's story.</li>
        <li>Contact page with phone, email, form, hours, and map.</li>
        <li>Service area pages if you cover multiple cities. <Link to="/learn/service-area-pages">Why these matter.</Link></li>
        <li>Schema markup (LocalBusiness, Service, FAQPage) — handled by a competent agency by default.</li>
        <li>SSL certificate, mobile testing, page speed check.</li>
      </ul>

      <h2>Week 4: launch + first SEO push</h2>
      <ul>
        <li>Submit sitemap to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Google Search Console</a>.</li>
        <li>Submit to Bing Webmaster Tools.</li>
        <li>Get listed on the top 10 citations: Yelp, Facebook, Apple Maps, BBB, Angi, Nextdoor, Yellow Pages, Foursquare, Chamber of Commerce, industry directory.</li>
        <li>Ask 5 happy customers for Google reviews. <Link to="/learn/get-more-reviews">How to ask the right way.</Link></li>
        <li>Set up Google Analytics 4 + call tracking.</li>
        <li>Write 1 blog post or service spotlight per month going forward.</li>
      </ul>

      <h2>What to skip in the first 30 days</h2>
      <ul>
        <li><strong>Paid ads.</strong> Don't run Google Ads to a site less than 2 weeks old — wait for tracking and content to stabilize.</li>
        <li><strong>Complex automations.</strong> Email funnels, chatbots, CRM integrations — add them after you have leads to nurture.</li>
        <li><strong>A perfect logo.</strong> Launch with a clean wordmark; refine the brand in month 4.</li>
        <li><strong>A blog with 20 posts.</strong> One thoughtful post beats ten thin ones every time.</li>
      </ul>

      <h2>Related guides</h2>
      <ul>
        <li><Link to="/learn/first-time-website-owner-guide">First-time website owner's guide</Link></li>
        <li><Link to="/learn/start-a-business-in-colorado">Starting a business in Colorado</Link></li>
        <li><Link to="/learn/web-design-cost-colorado">Web design cost in Colorado</Link></li>
        <li><Link to="/learn/marketing-budget">Setting a marketing budget</Link></li>
        <li><Link to="/learn/local-seo-guide">Local SEO guide for new businesses</Link></li>
        <li><Link to="/learn/franchise-location-website-guide">Franchise location website guide</Link></li>
      </ul>
      <h2>Work with us</h2>
      <ul>
        <li><Link to="/web-design">Web design</Link> — custom builds for Colorado businesses</li>
        <li><Link to="/seo">SEO</Link> and <Link to="/local-seo">local SEO</Link> — get found on Google</li>
        <li><Link to="/google-maps">Google Maps optimization</Link></li>
        <li><Link to="/pricing">Transparent pricing</Link></li>
      </ul>

    </ArticleLayout>
  );
}
